/*
 * video-slider.js
 * -------------------------------------------------------------------
 * Section-scoped engine for the "Video Slider" section
 * (sections/video-slider.liquid). Replaces the Swiper.js coverflow carousel
 * the original "SS - Video slider" used, running instead on Dawn's native
 * <slider-component> (SliderComponent, from assets/global.js) + CSS scroll-snap
 * — zero third-party dependency.
 *
 * Reproduces the original's look/behaviour:
 *   - CENTRED slides; the middle slide is the active one.
 *   - COVERFLOW SHRINK: the centred slide is full size and each slide further
 *     from centre scales down (0.9 per slide-width, matching the original
 *     coverflowEffect scale: 0.9). Pure scale — the section's Rotate setting
 *     was 0, and 3D rotate has no native equivalent, so it isn't reproduced.
 *   - INFINITE LOOP via duplicated slides: the real slides are cloned before
 *     and after so the row is always full and wraps seamlessly (the original
 *     did the same by rendering each slide 4×). At the seam, scrollLeft is
 *     reset by exactly one set-width, which is invisible because the copies are
 *     identical.
 *   - Autoplay ONLY the centred slide; every other slide (real or clone) loads
 *     its frame (via lazyload) but stays paused, so the duplicates show their
 *     video — only one video ever plays at a time, exactly like the original.
 *
 * Tradeoff: a fast momentum flick can occasionally reveal a tiny jump at the
 * loop seam (the cost of scroll-snap looping without a carousel library).
 *
 * Scoped to this section only — SUBCLASSES SliderComponent, does not modify it.
 */
(function () {
  if (typeof SliderComponent === 'undefined') return;
  if (typeof customElements === 'undefined') return;
  if (customElements.get('ss-video-loop')) return;

  var SCALE_BASE = 0.9; // neighbour = 90% size, next = 81%, ... (like coverflow 0.9)
  var SCALE_MIN = 0.5;
  var CLONE_SETS = 2; // full-set copies cloned on EACH side (loop buffer + fill)

  class SSVideoLoop extends SliderComponent {
    constructor() {
      super();
      if (!this.slider) return;

      // Wrap-around loop → the inherited update() must not disable the arrows.
      this.enableSliderLooping = true;
      // update() already ran once inside super() (before the flag above was set)
      // and, at scrollLeft 0, marked the PREV button `disabled` — which greys it
      // out AND stops it firing clicks (that's why only the left arrow was dead).
      // A looping slider never disables an arrow, so clear it now; future update()
      // calls return early because of enableSliderLooping.
      if (this.prevButton) this.prevButton.removeAttribute('disabled');
      if (this.nextButton) this.nextButton.removeAttribute('disabled');
      this.actionSel = this.getAttribute('data-action-sel');
      this.activeSlide = null;

      this.onSettle = this.onSettle.bind(this);
      this.slider.addEventListener(
        'scroll',
        () => {
          this.scheduleFrame(); // per-frame seam-reset + scaling (smooth)
          clearTimeout(this.settleTimer);
          this.settleTimer = setTimeout(this.onSettle, 120); // switch video on settle
        },
        { passive: true }
      );
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.remeasure(), 150);
      });

      // Build the loop once layout (offsets) is available.
      requestAnimationFrame(() => {
        this.buildLoop();
        this.updateScales();
        // Below-the-fold slider: defer the centre video's load + autoplay until
        // the section scrolls into view, so the (large) first video no longer
        // downloads on initial page load.
        var startInitial = () => { this.started = true; this.setActive(this.centerSlide(), true); };
        if ('IntersectionObserver' in window) {
          var io = new IntersectionObserver((entries, obs) => {
            for (var i = 0; i < entries.length; i++) {
              if (entries[i].isIntersecting) { obs.disconnect(); startInitial(); break; }
            }
          }, { rootMargin: '300px 0px' });
          // Only start watching once the page has loaded (or at the first
          // scroll): before that the images above have no height yet, so this
          // slider looked close to the screen and its first clip downloaded
          // on every visit. Waiting also lets the section swap in the 720p
          // files first (sections/video-slider.liquid).
          var watch = () => {
            window.removeEventListener('load', watch);
            window.removeEventListener('scroll', watch);
            io.observe(this);
          };
          if (document.readyState === 'complete') watch();
          else {
            window.addEventListener('load', watch);
            window.addEventListener('scroll', watch, { passive: true });
          }
        } else {
          startInitial();
        }
      });
    }

    liNodes() {
      return Array.from(this.slider.children).filter(function (el) {
        return el.tagName === 'LI';
      });
    }
    slides() {
      return this.liNodes();
    }

    buildLoop() {
      var items = this.liNodes();
      this.realCount = items.length;
      if (this.realCount < 2) {
        this.buildGeom();
        return;
      }

      var makeClone = function (el) {
        var c = el.cloneNode(true);
        c.removeAttribute('id');
        c.setAttribute('aria-hidden', 'true');
        c.classList.add('ss-clone');
        // Clones load their frame exactly like the real slides (via lazyload,
        // see refreshLazy) so the duplicates aren't blank — but they must never
        // AUTO-PLAY. Strip <video autoplay>; point cloned iframes at their
        // paused src (data-src2) so lazyload loads them paused.
        c.querySelectorAll('video').forEach(function (v) {
          v.removeAttribute('autoplay');
        });
        c.querySelectorAll('iframe').forEach(function (f) {
          var paused = f.getAttribute('data-src2');
          if (paused) {
            f.setAttribute('data-src', paused);
            f.removeAttribute('src');
          }
        });
        c.querySelectorAll('a, button, [tabindex]').forEach(function (n) {
          n.setAttribute('tabindex', '-1');
        });
        return c;
      };

      var first = items[0];
      for (var s = 0; s < CLONE_SETS; s++) {
        items.forEach(function (el) {
          this.slider.insertBefore(makeClone(el), first);
        }, this);
      }
      for (var s2 = 0; s2 < CLONE_SETS; s2++) {
        items.forEach(function (el) {
          this.slider.appendChild(makeClone(el));
        }, this);
      }

      this.firstRealIndex = CLONE_SETS * this.realCount;
      this.measure();
      this.recenter(true);
      this.refreshLazy();
    }

    // The clones are added AFTER lazyload initialises, so tell lazyload to
    // rescan; it then loads each clone's frame when it enters the viewport
    // (same behaviour as the real slides). Retries for up to 10s until
    // lazyLoadInstance exists (it is created on DOMContentLoaded, which can
    // land well after this on a slow phone).
    refreshLazy() {
      var update = function () {
        if (window.lazyLoadInstance && typeof window.lazyLoadInstance.update === 'function') {
          window.lazyLoadInstance.update();
          return true;
        }
        return false;
      };
      if (update()) return;
      var tries = 0;
      var iv = setInterval(function () {
        tries += 1;
        if (update() || tries > 100) clearInterval(iv);
      }, 100);
    }

    measure() {
      var lis = this.liNodes();
      var a = lis[this.firstRealIndex];
      var a1 = lis[this.firstRealIndex + 1];
      var b = lis[this.firstRealIndex + this.realCount];
      if (!a || !b) return;
      this.pitch = a1 ? a1.offsetLeft - a.offsetLeft : a.offsetWidth;
      this.setWidth = b.offsetLeft - a.offsetLeft; // one full-set advance
      this.realSpan = (this.realCount - 1) * this.pitch; // centre(first)..centre(last)
      this.anchor = a.offsetLeft + a.offsetWidth / 2 - this.slider.clientWidth / 2;
      this.buildGeom();
    }

    // Cache layout geometry so per-frame scaling never forces a reflow (scale
    // transforms don't change offsetLeft/offsetWidth, so this stays valid).
    buildGeom() {
      this.geom = this.liNodes().map(function (el) {
        return { el: el, center: el.offsetLeft + el.offsetWidth / 2, w: el.offsetWidth || 1 };
      });
    }

    recenter(instant) {
      if (this.anchor == null) return;
      this.jumpTo(this.anchor, instant);
    }

    jumpTo(x, instant) {
      this.adjusting = true;
      var prev = this.slider.style.scrollBehavior;
      if (instant) this.slider.style.scrollBehavior = 'auto';
      this.slider.scrollLeft = x;
      if (instant) this.slider.style.scrollBehavior = prev || '';
      requestAnimationFrame(() => {
        this.adjusting = false;
      });
    }

    seamReset() {
      if (this.adjusting || !this.setWidth || this.setWidth <= 0 || this.anchor == null) return;
      var x = this.slider.scrollLeft;
      // Keep a full clone-set of slack beyond the real set on each side, so
      // navigating one slide past an end never triggers an immediate reset
      // (which previously blocked leftward movement). Reset only deep in the
      // identical clones, where the setWidth jump is invisible.
      var leftT = this.anchor - this.setWidth;
      var rightT = this.anchor + this.realSpan + this.setWidth;
      if (x < leftT) this.jumpTo(x + this.setWidth, true);
      else if (x > rightT) this.jumpTo(x - this.setWidth, true);
    }

    scheduleFrame() {
      if (this.frameScheduled) return;
      this.frameScheduled = true;
      requestAnimationFrame(() => {
        this.frameScheduled = false;
        this.seamReset();
        this.updateScales();
      });
    }

    remeasure() {
      if (this.anchor == null) {
        this.buildGeom();
        this.updateScales();
        return;
      }
      var rel = this.slider.scrollLeft - this.anchor;
      this.measure();
      this.jumpTo(this.anchor + rel, true);
      this.updateScales();
    }

    // Scale each slide by its distance from centre, then slide it toward the
    // centre by the width the inner slides lost, so every visible gap stays at
    // the configured gap (scaling about each slide's own centre alone made the
    // outer gaps grow). The two slides either side of the viewport centre are
    // placed by scroll fraction, so the positions stay continuous while scrolling.
    updateScales() {
      if (!this.geom || !this.geom.length) return;
      var geom = this.geom;
      var n = geom.length;
      var vc = this.slider.scrollLeft + this.slider.clientWidth / 2;
      var scales = new Array(n);
      for (var i = 0; i < n; i++) {
        var s = Math.pow(SCALE_BASE, Math.abs(geom[i].center - vc) / geom[i].w);
        scales[i] = s < SCALE_MIN ? SCALE_MIN : s;
      }
      var gap = this.pitch ? Math.max(0, this.pitch - geom[0].w) : 0;
      var vis = new Array(n);
      var a = 0;
      while (a < n - 2 && geom[a + 1].center <= vc) a++;
      var b = Math.min(a + 1, n - 1);
      var span = geom[b].center - geom[a].center;
      var t = span > 0 ? Math.min(1, Math.max(0, (vc - geom[a].center) / span)) : 0;
      var d = (geom[a].w * scales[a] + geom[b].w * scales[b]) / 2 + gap;
      vis[a] = vc - t * d;
      if (b !== a) vis[b] = vis[a] + d;
      for (var r = b + 1; r < n; r++) {
        vis[r] = vis[r - 1] + (geom[r - 1].w * scales[r - 1] + geom[r].w * scales[r]) / 2 + gap;
      }
      for (var l = a - 1; l >= 0; l--) {
        vis[l] = vis[l + 1] - (geom[l + 1].w * scales[l + 1] + geom[l].w * scales[l]) / 2 - gap;
      }
      for (var k = 0; k < n; k++) {
        var g = geom[k];
        g.el.style.transformOrigin = 'center center';
        g.el.style.transform = 'translateX(' + (vis[k] - g.center).toFixed(2) + 'px) scale(' + scales[k] + ')';
      }
    }

    // Nearest-centre slide. Scaling from transform-origin: center preserves each
    // slide's centre, so getBoundingClientRect stays correct while scaled.
    centerSlide() {
      var lis = this.liNodes();
      if (!lis.length) return null;
      var rect = this.slider.getBoundingClientRect();
      var mid = rect.left + rect.width / 2;
      var best = null;
      var bestD = Infinity;
      lis.forEach(function (s) {
        var r = s.getBoundingClientRect();
        var d = Math.abs(r.left + r.width / 2 - mid);
        if (d < bestD) {
          bestD = d;
          best = s;
        }
      });
      return best;
    }

    onSettle() {
      this.setActive(this.centerSlide());
    }

    setActive(slide, force) {
      if (!slide) return;
      if (slide === this.activeSlide && !force) return;
      this.activeSlide = slide;
      var actionSel = this.actionSel;

      this.liNodes().forEach(function (other) {
        if (other === slide) return;
        var video = other.querySelector('video');
        var frame = other.querySelector('iframe');
        var btn = actionSel ? other.querySelector(actionSel) : null;
        if (video) video.pause();
        if (frame && frame.getAttribute('src')) {
          var src2 = frame.getAttribute('data-src2');
          if (src2) frame.src = src2;
        }
        if (btn) btn.classList.remove('active');
      });

      this.play(slide);
    }

    play(slide) {
      // Nothing loads or plays until the slider has come into view (startInitial).
      // Building the loop scrolls the track, which runs onSettle -> setActive ->
      // play on page load and used to start the first clip's download early.
      if (!this.started) return;
      var video = slide.querySelector('video');
      var frame = slide.querySelector('iframe');
      var btn = this.actionSel ? slide.querySelector(this.actionSel) : null;
      if (frame) {
        var src1 = frame.getAttribute('data-src1');
        if (src1) frame.src = src1;
      }
      if (video) {
        if (!video.getAttribute('src')) {
          var dsrc = video.getAttribute('data-src');
          if (dsrc) video.src = dsrc;
        }
        video.play().catch(function () {});
      }
      if (btn) btn.classList.add('active');
    }

    // Advance one slide; the seam-reset handles wrapping past the ends.
    onButtonClick(event) {
      event.preventDefault();
      var lis = this.liNodes();
      if (!lis.length) return;
      var idx = lis.indexOf(this.centerSlide());
      if (idx < 0) idx = 0;
      var target =
        event.currentTarget.name === 'next'
          ? lis[Math.min(idx + 1, lis.length - 1)]
          : lis[Math.max(idx - 1, 0)];
      this.scrollToSlide(target);
      this.setActive(target);
    }

    scrollToSlide(slide) {
      var rect = this.slider.getBoundingClientRect();
      var r = slide.getBoundingClientRect();
      var delta = r.left + r.width / 2 - (rect.left + rect.width / 2);
      this.slider.scrollTo({ left: this.slider.scrollLeft + delta, behavior: 'smooth' });
    }
  }

  customElements.define('ss-video-loop', SSVideoLoop);
})();
